import { api } from '@/api/instance';
import { createLogger, LOG_NAMESPACES } from '@/utils/logger';
import { getErrorMessage } from '../axios.config';

const logger = createLogger(LOG_NAMESPACES.API);

export const createCollection = async (body: Record<string, unknown>) => {
  try {
    const response = await api.post('/chaincode/collection', body, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Create collection error:', message);
    throw new Error(message);
  }
};

export const getAllCollections = async (id: string | undefined) => {
  try {
    const response = await api.get(`/chaincode/collection/all?dataset-id=${id}`, {
      withCredentials: true,
    });
    return response.data.result;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Get all collections error:', message);
    throw new Error(message);
  }
};

export const getCollectionById = async (id: string | undefined) => {
  try {
    const response = await api.get(`/chaincode/collection/${id}`, {
      withCredentials: true,
      timeout: 5000,
    });
    return response.data.result;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Get collection by ID error:', message);
    throw new Error(message);
  }
};

export const updateCollection = async (body: { id: string; schema: any[]; name: string }) => {
  try {
    const response = await api.patch(`/chaincode/collection/${body.id}`, body, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Update collection error:', message);
    throw new Error(message);
  }
};

export const deleteCollection = async (id: string) => {
  try {
    const response = await api.delete(`/chaincode/collection/${id}`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Delete collection error:', message);
    throw new Error(message);
  }
};

export const getCollectionIndexes = async (collectionId: string | undefined) => {
  try {
    const response = await api.get(`/chaincode/collection/${collectionId}/index/all`, {
      withCredentials: true,
    });
    return response.data.result;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Get collection indexes error:', message);
    throw new Error(message);
  }
};

export const createCollectionIndex = async (body: Record<string, unknown>) => {
  try {
    const response = await api.post(`/chaincode/collection/${body.collId}/index`, body, {
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Create collection index error:', message);
    throw new Error(message);
  }
};

export const deleteCollectionIndex = async (body: { collId: string; indexes: string[] }) => {
  try {
    const response = await api.delete(`/chaincode/collection/${body.collId}/index`, {
      data: { indexes: body.indexes },
      withCredentials: true,
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Delete collection index error:', message);
    throw new Error(message);
  }
};

export const importFileToCollection = async (
  body: {
    datasetId: string;
    file: Blob;
    mimeType: string;
    fileName: string;
    collectionName: string;
  },
  onProgress?: (progress: number) => void,
) => {
  logger.info(`Starting file import: ${body.fileName}, type: ${body.mimeType}, size: ${body.file.size} bytes`);
  try {
    const formData = new FormData();
    formData.append('file', body.file, `${body.fileName}.gz`);
    formData.append('dataset_id', body.datasetId);
    formData.append('mime_type', body.mimeType);
    formData.append('collection_name', body.collectionName);
    logger.debug('FormData prepared for upload');

    let timeout = 60000;
    const { size } = body.file;
    if (size > 5 * 1024 * 1024) {
      timeout = 180000;
    } else if (size > 1024 * 1024) {
      timeout = 120000;
    } else if (size > 512 * 1024) {
      timeout = 90000;
    }
    logger.debug(`Calculated timeout: ${timeout / 1000} seconds for file size ${size} bytes`);

    const response = await api.post('/rubicon/zip/import', formData, {
      withCredentials: true,
      timeout,
      validateStatus: (status) => (status >= 200 && status < 300) || status === 204,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total && progressEvent.loaded) {
          const percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), 95);
          onProgress(percentCompleted);
        }
      },
    });

    if (onProgress) {
      onProgress(100);
    }

    if (response.status === 204) {
      logger.info('File import successful with status 204');
      return { success: true, message: 'File uploaded successfully' };
    }

    logger.info(`File import successful with status ${response.status}`);
    return response.data || { success: true };
  } catch (error) {
    logger.error('Error during file import', {
      error: (error as Error).message,
      fileName: body.fileName,
    });

    const message = getErrorMessage(error);
    logger.error('Import file to collection error:', message);
    throw new Error(message);
  }
};

/**
 * @deprecated Use `importFileToCollection` instead. This endpoint is deprecated.
 */
export const importCSVToCollection = async (
  body: {
    datasetId: string;
    headline: boolean;
    file: File;
  },
  onProgress?: (progress: number) => void,
) => {
  let timeout = 60000;
  try {
    const formData = new FormData();
    formData.append('file', body.file);
    formData.append('dataset_id', body.datasetId);

    logger.debug('CSV Import Request', {
      fileName: body.file.name,
      fileSize: body.file.size,
      fileType: body.file.type,
      datasetId: body.datasetId,
      headline: body.headline,
    });

    if (body.file.size > 5 * 1024 * 1024) {
      timeout = 180000;
    } else if (body.file.size > 1024 * 1024) {
      timeout = 120000;
    } else if (body.file.size > 512 * 1024) {
      timeout = 90000;
    }

    const fileSizeMB = (body.file.size / 1024 / 1024).toFixed(2);
    logger.debug(`Starting upload of ${fileSizeMB}MB file with ${timeout / 1000}s timeout`);

    const response = await api.post('/rubicon/csv/import', formData, {
      withCredentials: true,
      timeout,
      validateStatus: (status) => {
        return (status >= 200 && status < 300) || status === 204;
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total && progressEvent.loaded) {
          const percentCompleted = Math.min(Math.round((progressEvent.loaded * 100) / progressEvent.total), 95);
          logger.trace(`Upload progress: ${progressEvent.loaded}/${progressEvent.total} (${percentCompleted}%)`);
          onProgress(percentCompleted);
        }
      },
    });

    logger.info(`Upload completed - Status: ${response.status}`);

    if (onProgress) {
      onProgress(100);
    }

    if (response.status === 204) {
      return { success: true, message: 'File uploaded successfully' };
    }

    return response.data || { success: true };
  } catch (error) {
    logger.error('Upload error details', {
      error,
      fileSize: body.file.size,
      fileName: body.file.name,
      fileType: body.file.type,
      datasetId: body.datasetId,
      errorMessage: error instanceof Error ? error.message : 'Unknown error',
    });

    const message = getErrorMessage(error);
    logger.error('Import CSV to collection error:', message);
    throw new Error(message);
  }
};

/**
 * @deprecated Use `importFileToCollection` instead. This endpoint is deprecated.
 */
export const importCSVToCollectionChunked = async (
  body: {
    datasetId: string;
    headline: boolean;
    file: File;
    chunk: Blob;
    chunkIndex: number;
    totalChunks: number;
    fileName: string;
  },
  onProgress?: (progress: number) => void,
) => {
  try {
    const formData = new FormData();
    formData.append('file', body.chunk, body.fileName);
    formData.append('dataset_id', body.datasetId);
    formData.append('chunk_index', body.chunkIndex.toString());
    formData.append('total_chunks', body.totalChunks.toString());
    formData.append('original_filename', body.fileName);

    const timeout = 300000;

    logger.info(`Uploading chunk ${body.chunkIndex + 1}/${body.totalChunks} for file ${body.fileName}`);

    const response = await api.post('/rubicon/csv/import/chunk', formData, {
      withCredentials: true,
      timeout,
      validateStatus: (status) => {
        return (status >= 200 && status < 300) || status === 204;
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total && progressEvent.loaded) {
          const chunkProgress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(chunkProgress);
        }
      },
    });

    logger.info(`Chunk ${body.chunkIndex + 1}/${body.totalChunks} uploaded - Status: ${response.status}`);

    return response.data || { success: true };
  } catch (error) {
    logger.error('Chunk upload error', {
      error,
      chunkIndex: body.chunkIndex,
      totalChunks: body.totalChunks,
      fileName: body.fileName,
    });

    const message = getErrorMessage(error);
    logger.error('Import CSV to collection chunked error:', message);
    throw new Error(message);
  }
};

/**
 * @deprecated Use `importFileToCollection` instead. This endpoint is deprecated.
 */
export const importJSONToCollection = async (
  body: {
    datasetId: string;
    file: File;
  },
  onProgress?: (progress: number) => void,
) => {
  try {
    const formData = new FormData();
    formData.append('file', body.file);
    formData.append('dataset_id', body.datasetId);

    const response = await api.post('/rubicon/json/import', formData, {
      withCredentials: true,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });
    return response.data;
  } catch (error) {
    const message = getErrorMessage(error);
    logger.error('Import JSON to collection error:', message);
    throw new Error(message);
  }
};

// Initialize chunked upload — returns collection_id for subsequent chunk uploads
export interface InitChunkUploadResult {
  id: string;
  name: string;
  owner: string;
  dataset_id: string;
  path_reference: string;
  doc_type: string;
  chunk_import: {
    id: string;
    doc_type: string;
    collection_id: string;
    uploaded_chunks: number;
    total_chunks: number;
    received_rows: number;
    ipfs_chunks: string[];
  };
}

interface InitChunkUploadApiResponse {
  result: InitChunkUploadResult;
  error: unknown;
}

export const initializeChunkUpload = async (body: {
  collectionName: string;
  datasetId: string;
  totalChunks: number;
  totalDocuments: number;
}): Promise<{ collectionId: string; result: InitChunkUploadResult }> => {
  logger.info(
    `Initializing chunk upload: collection="${body.collectionName}", dataset="${body.datasetId}", totalChunks=${body.totalChunks}, totalDocuments=${body.totalDocuments}`,
  );

  try {
    const response = await api.post<InitChunkUploadApiResponse>(
      '/rubicon/zip/import/chunks',
      {
        collection_name: body.collectionName,
        dataset_id: body.datasetId,
        total_chunks: body.totalChunks,
        total_documents: body.totalDocuments,
      },
      {
        withCredentials: true,
        timeout: 60000,
      },
    );

    const collectionId = response.data.result.id;
    logger.info(`Chunk upload initialized — collection_id: ${collectionId}`);
    return { collectionId, result: response.data.result };
  } catch (error) {
    logger.error('Chunk upload initialization failed', { error: (error as Error).message });

    const message = getErrorMessage(error);
    logger.error('Initialize chunk upload error:', message);
    throw new Error(message);
  }
};

// Upload a single chunk via PATCH — must be called serially (wait for success before next)
export const uploadChunk = async (
  body: {
    collectionId: string;
    chunk: File;
    chunkIndex: number;
    totalChunks: number;
    hash: string;
  },
  onProgress?: (progress: number) => void,
): Promise<void> => {
  logger.info(
    `Uploading chunk ${body.chunkIndex + 1}/${body.totalChunks} — hash: ${body.hash}, file: ${body.chunk.name}, size: ${body.chunk.size} bytes`,
  );

  try {
    const formData = new FormData();
    formData.append('file', body.chunk);

    await api.patch(`/rubicon/zip/import/chunks/${body.collectionId}`, formData, {
      withCredentials: true,
      timeout: 300000,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(percentCompleted);
        }
      },
    });

    logger.info(`Chunk ${body.chunkIndex + 1}/${body.totalChunks} uploaded successfully — hash: ${body.hash}`);
  } catch (error) {
    logger.error('Chunk upload failed', {
      error: (error as Error).message,
      chunkIndex: body.chunkIndex,
      hash: body.hash,
      collectionId: body.collectionId,
    });

    const message = getErrorMessage(error);
    logger.error('Upload chunk error:', message);
    throw new Error(message);
  }
};
